/** Состояние авторизации: одна учётная запись на всю библиотеку. */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { api, getToken, setToken } from './api'

interface AuthValue {
  username: string | null
  configured: boolean | null
  loading: boolean
  login: (username: string, password: string) => Promise<void>
  setup: (username: string, password: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [username, setUsername] = useState<string | null>(null)
  const [configured, setConfigured] = useState<boolean | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    async function bootstrap() {
      try {
        const status = await api.authStatus()
        if (cancelled) return
        setConfigured(status.configured)
        if (status.configured && getToken()) {
          try {
            const me = await api.me()
            if (!cancelled) setUsername(me.username)
          } catch {
            // Токен протух — просто просим войти заново.
            setToken(null)
          }
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void bootstrap()
    return () => {
      cancelled = true
    }
  }, [])

  const login = useCallback(async (name: string, password: string) => {
    const result = await api.login(name, password)
    setToken(result.token)
    setUsername(result.username)
    setConfigured(true)
  }, [])

  const setup = useCallback(async (name: string, password: string) => {
    const result = await api.setup(name, password)
    setToken(result.token)
    setUsername(result.username)
    setConfigured(true)
  }, [])

  const logout = useCallback(async () => {
    try {
      await api.logout()
    } finally {
      setToken(null)
      setUsername(null)
    }
  }, [])

  const value = useMemo(
    () => ({ username, configured, loading, login, setup, logout }),
    [username, configured, loading, login, setup, logout],
  )
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth вне AuthProvider')
  return value
}
